import type { ReactNode } from 'react';

import { EmptyState, Skeleton } from '../../../../ui/index.js';

/**
 * Thẻ của một khối trên ba tab gộp, kèm **nhãn độ phủ**.
 *
 * Nhãn đó là lý do khối này tồn tại thay vì gõ `<section>` ở từng chỗ: mọi con
 * số gộp phải nói nó gộp trên bao nhiêu kênh. Một tỷ lệ "64 % xem bằng điện
 * thoại" tính trên 12 trong 16 kênh mà không nói ra là một con số sai, và sai
 * theo cách không ai phát hiện được.
 *
 * Ba trạng thái **không được trộn**:
 *
 * - `chuaCoBanLuu` → khối chưa có số liệu cho kỳ này (quốc gia và độ tuổi chỉ
 *   có ở những kỳ job `analytics-ham-nong` đã lưu). Mời chọn kỳ khác.
 * - rỗng mà **đã** có bản lưu → kênh thật sự không có số liệu đó.
 * - có số → bày, và nói độ phủ nếu thiếu kênh.
 */

export interface KhoiGopProps {
  tieuDe: string;
  /** Dòng phụ cố định dưới tiêu đề (trước nhãn độ phủ). */
  ghiChu?: string;
  soKenhCoSoLieu: number;
  soKenhTrongPhamVi: number;
  /** `true` = khối chưa có bản lưu cho kỳ này, không phải "không có số liệu". */
  chuaCoBanLuu?: boolean;
  dangTai: boolean;
  /** `true` khi không có dòng nào để bày. */
  rong: boolean;
  /** Nội dung góc phải tiêu đề (chú thích, link…). */
  phai?: ReactNode;
  caoKhungXuong?: string;
  children: ReactNode;
}

export function KhoiGop({
  tieuDe,
  ghiChu,
  soKenhCoSoLieu,
  soKenhTrongPhamVi,
  chuaCoBanLuu = false,
  dangTai,
  rong,
  phai,
  caoKhungXuong = '12rem',
  children,
}: KhoiGopProps) {
  return (
    <section className="flex flex-col rounded-the border border-vien bg-be-mat p-6">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
        <div>
          <h2 className="text-tieu-de-khoi font-semibold text-chu">{tieuDe}</h2>
          {(ghiChu !== undefined || !dangTai) && (
            <p className="mt-1 text-phu text-chu-mo">
              {[ghiChu, dangTai ? '' : nhanDoPhu(soKenhCoSoLieu, soKenhTrongPhamVi)]
                .filter((x) => x !== undefined && x !== '')
                .join(' · ')}
            </p>
          )}
        </div>
        {phai}
      </div>

      {dangTai ? (
        <Skeleton cao={caoKhungXuong} />
      ) : chuaCoBanLuu ? (
        <EmptyState
          title="Chưa có số liệu cho kỳ này"
          description="Số liệu người xem chỉ có ở các kỳ đã lấy sẵn (7/28/90/365 ngày và ba tháng gần nhất). Chọn một kỳ khác ở ô chọn kỳ phía trên."
        />
      ) : rong ? (
        <EmptyState title="Không có số liệu trong kỳ này" />
      ) : (
        children
      )}
    </section>
  );
}

/**
 * `Gộp từ 12/16 kênh` — và **im lặng khi đủ cả**.
 *
 * Nói "16/16 kênh" ở mọi khối là một dòng chữ không mang tin, lặp bốn lần trên
 * một trang. Chỉ lên tiếng khi phép gộp thiếu kênh, vì đúng lúc đó con số mới
 * cần một lời giải thích.
 */
export function nhanDoPhu(co: number, tong: number): string {
  if (tong === 0 || co >= tong) return '';
  return `Gộp từ ${String(co)}/${String(tong)} kênh có số liệu`;
}

/** Thanh ngang tỷ lệ — dùng ở mọi khối của ba tab, một thước duy nhất. */
export function ThanhNgang({
  phanTram,
  mau = 'var(--color-nhan)',
}: {
  phanTram: number;
  mau?: string;
}) {
  return (
    <span aria-hidden="true" className="block h-2.5 overflow-hidden rounded-full bg-neutral-nen">
      <span
        className="block h-2.5 rounded-full"
        style={{
          width: `${String(Math.min(Math.max(phanTram, 0), 100))}%`,
          background: mau,
        }}
      />
    </span>
  );
}
